/**
 * Export utilities — CSV and PDF generation without external PDF libraries.
 * Uses browser-native APIs for CSV and a print-friendly HTML approach for PDF.
 */

// ─── CSV Export ───────────────────────────────────────────────────────────────

/**
 * Convert an array of objects to a CSV string.
 * @param {object[]} rows
 * @param {string[]} columns - Column keys to include
 * @param {object} headers - Map of column key → display header
 */
export function toCSV(rows, columns, headers = {}) {
  const headerRow = columns?.map(col => `"${headers?.[col] || col}"`)?.join(',');
  const dataRows = rows?.map(row =>
    columns?.map(col => {
      const val = row?.[col];
      if (val === null || val === undefined) return '""';
      const str = String(val)?.replace(/"/g, '""');
      return `"${str}"`;
    })?.join(',')
  );
  return [headerRow, ...dataRows]?.join('\n');
}

/**
 * Trigger a CSV file download in the browser.
 * @param {string} csvContent
 * @param {string} filename
 */
export function downloadCSV(csvContent, filename = 'export.csv') {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link?.setAttribute('download', filename);
  document.body?.appendChild(link);
  link?.click();
  document.body?.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * One-step helper: export rows to CSV file.
 */
export function exportToCSV(rows, columns, headers, filename) {
  const csv = toCSV(rows, columns, headers);
  downloadCSV(csv, filename);
}

// ─── PDF Export (print-based) ─────────────────────────────────────────────────

/**
 * Generate a print-friendly HTML page and trigger the browser print dialog.
 * This produces a PDF when the user selects "Save as PDF" in the print dialog.
 *
 * @param {object} options
 * @param {string} options.title - Report title
 * @param {string} options.subtitle - Optional subtitle / date range
 * @param {object[]} options.rows - Data rows
 * @param {string[]} options.columns - Column keys
 * @param {object} options.headers - Column key → display header
 * @param {object[]} options.summaryCards - Optional [{ label, value }] summary cards
 */
export function exportToPDF({ title, subtitle = '', rows, columns, headers = {}, summaryCards = [] }) {
  const now = new Date()?.toLocaleString();

  const summaryHTML = summaryCards?.length
    ? `<div class="summary">
        ${summaryCards?.map(c => `
          <div class="card">
            <div class="card-value">${c?.value}</div>
            <div class="card-label">${c?.label}</div>
          </div>`)?.join('')}
       </div>`
    : '';

  const tableHeaders = columns?.map(col => `<th>${headers?.[col] || col}</th>`)?.join('');
  const tableRows = rows?.map(row =>
    `<tr>${columns?.map(col => {
      const val = row?.[col];
      return `<td>${val === null || val === undefined ? '—' : String(val)}</td>`;
    })?.join('')}</tr>`
  )?.join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${title}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; font-size: 12px; color: #1e293b; padding: 32px; }
    .header { border-bottom: 2px solid #6366f1; padding-bottom: 16px; margin-bottom: 24px; }
    .header h1 { font-size: 22px; font-weight: 700; color: #4f46e5; }
    .header p  { font-size: 11px; color: #64748b; margin-top: 4px; }
    .meta { font-size: 10px; color: #94a3b8; margin-top: 4px; }
    .summary { display: flex; gap: 16px; margin-bottom: 24px; flex-wrap: wrap; }
    .card { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 12px 20px; min-width: 120px; }
    .card-value { font-size: 20px; font-weight: 700; color: #4f46e5; }
    .card-label { font-size: 10px; color: #64748b; margin-top: 2px; text-transform: uppercase; letter-spacing: 0.05em; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #4f46e5; color: white; padding: 8px 10px; text-align: left; font-size: 11px; font-weight: 600; }
    td { padding: 7px 10px; border-bottom: 1px solid #f1f5f9; font-size: 11px; }
    tr:nth-child(even) td { background: #f8fafc; }
    @media print { body { padding: 16px; } }
  </style>
</head>
<body>
  <div class="header">
    <h1>${title}</h1>
    ${subtitle ? `<p>${subtitle}</p>` : ''}
    <p class="meta">Generated: ${now} &nbsp;|&nbsp; ${rows?.length} records</p>
  </div>
  ${summaryHTML}
  <table>
    <thead><tr>${tableHeaders}</tr></thead>
    <tbody>${tableRows}</tbody>
  </table>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) { alert('Please allow pop-ups to export PDF.'); return; }
  win?.document?.write(html);
  win?.document?.close();
  win?.focus();
  setTimeout(() => { win?.print(); }, 500);
}
